import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { priceConsultationAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  MagnifyingGlass,
  ChartLineUp,
  MapPin,
  Package,
  CircleNotch,
  Lock,
  Crown,
  TrendUp,
  TrendDown,
  Calendar,
  ArrowRight,
  CaretUp,
  CaretDown,
  Minus,
  FilePdf,
  Bell,
  Trash,
  Plus,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const MEXICAN_STATES = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

const formatPrice = (price) => {
  if (!price && price !== 0) return 'N/A';
  return `$${price.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-3 shadow-lg">
      <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{formatMonth(label)}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: {formatPrice(p.value)}
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon, subtext }) => (
  <div className={`p-4 border-l-4 ${color} bg-zinc-50 dark:bg-zinc-800/50`}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-4 h-4 text-zinc-400" />}
      <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-xl font-bold text-zinc-900 dark:text-zinc-100">{value}</p>
    {subtext && <p className="text-xs text-zinc-500 mt-1">{subtext}</p>}
  </div>
);

const PriceConsultationPage = () => {
  const { user } = useAuth();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [categories, setCategories] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedState, setSelectedState] = useState('');

  const [searchResults, setSearchResults] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [alertPrice, setAlertPrice] = useState('');
  const [alertType, setAlertType] = useState('below');
  const [creatingAlert, setCreatingAlert] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(false);

  useEffect(() => {
    checkSubscription();
    fetchCategories();
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await priceConsultationAPI.getAlerts();
      setAlerts(res.data.alerts || []);
    } catch (e) { /* subscription may not be active */ }
  };

  const checkSubscription = async () => {
    try {
      const response = await priceConsultationAPI.getSubscriptionStatus();
      setHasSubscription(response.data.has_active_subscription);
    } catch (error) {
      console.error('Failed to check subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await priceConsultationAPI.getCategories();
      let cats = [];
      if (Array.isArray(response.data)) {
        cats = response.data.map(c => typeof c === 'object' ? c.name : c);
      } else if (response.data.categories) {
        cats = response.data.categories.map(c => typeof c === 'object' ? c.name : c);
      }
      setCategories(cats.filter(c => c));
    } catch (error) {
      setCategories([]);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && !selectedCategory) {
      toast.error('Ingresa un término de búsqueda o selecciona una categoría');
      return;
    }
    setSearching(true);
    setSelectedMaterial(null);
    setPriceData(null);
    try {
      const params = {
        q: searchQuery.trim(),
        category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : undefined,
        state: selectedState && selectedState !== 'all' ? selectedState : undefined,
      };
      const response = await priceConsultationAPI.search(params);
      setSearchResults(response.data.materials || []);
      if (response.data.materials?.length === 0) {
        toast.info('No se encontraron materiales con esos criterios');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Se requiere una suscripción activa');
        setHasSubscription(false);
      } else {
        toast.error('Error al buscar materiales');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSelectMaterial = async (material) => {
    setSelectedMaterial(material);
    setSelectedBrand('');
    setBrands([]);
    setLoadingDetail(true);
    setLoadingBrands(true);
    try {
      const params = {
        material_name: material.material_name,
        category: material.category,
        unit: material.unit,
        state: selectedState && selectedState !== 'all' ? selectedState : undefined,
      };
      const [priceRes, brandsRes] = await Promise.all([
        priceConsultationAPI.getPriceData(params),
        priceConsultationAPI.getBrands(params),
      ]);
      setPriceData(priceRes.data);
      setBrands(brandsRes.data.brands || []);
    } catch (error) {
      toast.error('Error al obtener historial de precios');
    } finally {
      setLoadingDetail(false);
      setLoadingBrands(false);
    }
  };

  const handleSelectBrand = async (brandName) => {
    const newBrand = brandName === selectedBrand ? '' : brandName;
    setSelectedBrand(newBrand);
    setLoadingDetail(true);
    try {
      const params = {
        material_name: selectedMaterial.material_name,
        category: selectedMaterial.category,
        unit: selectedMaterial.unit,
        state: selectedState && selectedState !== 'all' ? selectedState : undefined,
        brand: newBrand || undefined,
      };
      const response = await priceConsultationAPI.getPriceData(params);
      setPriceData(response.data);
    } catch (error) {
      toast.error('Error al filtrar por marca');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedMaterial) return;
    setDownloadingPDF(true);
    try {
      const params = {
        material_name: selectedMaterial.material_name,
        category: selectedMaterial.category,
        unit: selectedMaterial.unit,
        state: selectedState && selectedState !== 'all' ? selectedState : undefined,
        brand: selectedBrand || undefined,
      };
      const response = await priceConsultationAPI.downloadPDF(params);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_precios_${selectedMaterial.material_name.slice(0, 30).replace(/ /g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!selectedMaterial || !alertPrice) return;
    setCreatingAlert(true);
    try {
      await priceConsultationAPI.createAlert({
        material_name: selectedMaterial.material_name,
        category: selectedMaterial.category,
        unit: selectedMaterial.unit,
        target_price: parseFloat(alertPrice),
        state: selectedState && selectedState !== 'all' ? selectedState : null,
        alert_type: alertType,
      });
      toast.success('Alerta de precio creada');
      setShowAlertForm(false);
      setAlertPrice('');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear alerta');
    } finally {
      setCreatingAlert(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await priceConsultationAPI.deleteAlert(alertId);
      toast.success('Alerta eliminada');
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch (error) {
      toast.error('Error al eliminar alerta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12" data-testid="price-consultation-locked">
        <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-orange-400" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
          Consultor de Precios
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400 mb-3">
          Accede a más de 2 años de historial de precios de materiales de construcción en todo México.
        </p>
        <p className="text-zinc-500 dark:text-zinc-500 text-sm mb-8">
          Gráficas históricas, promedios mensuales, comparativas por estado y datos actualizados con cada cotización de la plataforma.
        </p>
        <Link to="/subscriptions">
          <Button className="btn-primary rounded-none" data-testid="subscribe-btn">
            <Crown className="w-5 h-5 mr-2" />
            Ver Planes de Suscripción
          </Button>
        </Link>
      </div>
    );
  }

  const chartData = priceData?.chart_data || [];
  const summary = priceData?.summary || {};
  const availableStates = priceData?.available_states || [];

  // Compute price trend from chart data
  let trendPct = 0;
  if (chartData.length >= 2) {
    const first = chartData[0].avg_price;
    const last = chartData[chartData.length - 1].avg_price;
    if (first > 0) trendPct = ((last - first) / first * 100);
  }

  return (
    <div className="space-y-6" data-testid="price-consultation-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ChartLineUp className="w-6 h-6 text-orange-500" />
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Consultor de Precios</h1>
          </div>
          <p className="text-zinc-600 dark:text-zinc-400 text-sm">Busca y analiza precios históricos de materiales de construcción</p>
        </div>
        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded-none" data-testid="subscription-badge">
          <Crown className="w-4 h-4 mr-1" />
          Suscripción Activa
        </Badge>
      </div>

      {/* Search */}
      <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="search-card">
        <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
          <CardTitle className="text-base">Buscar Materiales</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <Label>Material</Label>
              <div className="relative mt-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Ej: Cemento, Varilla, Block..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 rounded-none"
                  data-testid="material-search-input"
                />
              </div>
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="rounded-none mt-1" data-testid="category-select">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-[300px]">
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="rounded-none mt-1" data-testid="state-select">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-[300px]">
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {MEXICAN_STATES.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={handleSearch}
            disabled={searching}
            className="btn-primary rounded-none mt-4"
            data-testid="search-btn"
          >
            {searching ? (
              <CircleNotch className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <MagnifyingGlass className="w-5 h-5 mr-2" />
                Buscar
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Materials list */}
        <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="materials-list">
          <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
            <CardTitle className="text-base">Materiales ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {searchResults.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p className="text-sm">Realiza una búsqueda para ver materiales</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
                  {searchResults.map((material, index) => (
                    <div
                      key={`${material.material_name}-${material.unit}-${index}`}
                      onClick={() => handleSelectMaterial(material)}
                      className={`p-4 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors ${
                        selectedMaterial?.material_name === material.material_name &&
                        selectedMaterial?.unit === material.unit
                          ? 'bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400'
                          : ''
                      }`}
                      data-testid={`material-item-${index}`}
                    >
                      <p className="font-medium text-zinc-900 dark:text-zinc-100 text-sm line-clamp-2">
                        {material.material_name}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="rounded-none text-xs">{material.unit}</Badge>
                        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{formatPrice(material.latest_price)}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-zinc-400">{material.category}</p>
                        <span className="text-xs text-zinc-400">{material.record_count} registros</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Detail panel */}
        <div className="lg:col-span-2 space-y-6">
          {!selectedMaterial ? (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
              <CardContent className="p-6">
                <div className="text-center py-16 text-zinc-500">
                  <ChartLineUp className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
                  <p>Selecciona un material para ver su análisis completo</p>
                </div>
              </CardContent>
            </Card>
          ) : loadingDetail ? (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
              <CardContent className="flex items-center justify-center py-20">
                <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" />
              </CardContent>
            </Card>
          ) : priceData ? (
            <>
              {/* Material header */}
              <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="material-detail">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{selectedMaterial.material_name}</h3>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="rounded-none text-xs">{selectedMaterial.category}</Badge>
                        <Badge variant="outline" className="rounded-none text-xs">{selectedMaterial.unit}</Badge>
                        {selectedBrand && (
                          <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded-none text-xs">
                            {selectedBrand}
                          </Badge>
                        )}
                        {priceData.state && (
                          <Badge variant="outline" className="rounded-none text-xs">
                            <MapPin className="w-3 h-3 mr-1" />{priceData.state}
                          </Badge>
                        )}
                        <Badge variant="outline" className="rounded-none text-xs">
                          <Calendar className="w-3 h-3 mr-1" />{summary.total_records} registros
                        </Badge>
                        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-none text-xs">
                          IVA 16% incluido
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="rounded-none border-orange-300 text-orange-700"
                        onClick={handleDownloadPDF}
                        disabled={downloadingPDF}
                        data-testid="download-price-pdf-btn"
                      >
                        {downloadingPDF ? <CircleNotch className="w-4 h-4 animate-spin" /> : <><FilePdf className="w-4 h-4 mr-2" />PDF</>}
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-none border-blue-300 text-blue-700"
                        onClick={() => setShowAlertForm(!showAlertForm)}
                        data-testid="toggle-alert-form-btn"
                      >
                        <Bell className="w-4 h-4 mr-2" />Alerta
                      </Button>
                    </div>
                  </div>

                  {/* Alert creation form */}
                  {showAlertForm && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" data-testid="alert-form">
                      <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                        <Bell className="w-4 h-4" />Crear Alerta de Precio
                      </h4>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                        Recibe un correo cuando el precio de <strong>{selectedMaterial.material_name}</strong> alcance tu objetivo.
                      </p>
                      <div className="flex items-end gap-3 flex-wrap">
                        <div>
                          <Label className="text-xs">Notificarme cuando el precio esté</Label>
                          <Select value={alertType} onValueChange={setAlertType}>
                            <SelectTrigger className="rounded-none w-40 mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent className="rounded-none">
                              <SelectItem value="below">Por debajo de</SelectItem>
                              <SelectItem value="above">Por encima de</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Precio objetivo (MXN)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={alertPrice}
                            onChange={(e) => setAlertPrice(e.target.value)}
                            className="rounded-none w-36 mt-1"
                            placeholder="0.00"
                            data-testid="alert-price-input"
                          />
                        </div>
                        <Button
                          className="btn-primary rounded-none"
                          onClick={handleCreateAlert}
                          disabled={creatingAlert || !alertPrice}
                          data-testid="create-alert-btn"
                        >
                          {creatingAlert ? <CircleNotch className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Crear</>}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats cards - Promedio 3 meses */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="price-stats">
                <StatCard
                  label={selectedBrand ? `Prom. 3m (${selectedBrand})` : "Promedio 3 meses"}
                  value={formatPrice(summary.avg_price_3m)}
                  color="border-orange-400"
                  icon={ChartLineUp}
                  subtext={`${summary.records_3m} registros`}
                />
                <StatCard
                  label="Mínimo 3 meses"
                  value={formatPrice(summary.min_price_3m)}
                  color="border-green-400"
                  icon={CaretDown}
                />
                <StatCard
                  label="Máximo 3 meses"
                  value={formatPrice(summary.max_price_3m)}
                  color="border-red-400"
                  icon={CaretUp}
                />
                <StatCard
                  label="Tendencia 2 años"
                  value={`${trendPct >= 0 ? '+' : ''}${trendPct.toFixed(1)}%`}
                  color={trendPct >= 0 ? 'border-red-400' : 'border-green-400'}
                  icon={trendPct >= 0 ? TrendUp : TrendDown}
                  subtext={chartData.length > 0 ? `${formatMonth(chartData[0].month)} — ${formatMonth(chartData[chartData.length - 1].month)}` : ''}
                />
              </div>

              {/* Brand Selector */}
              {brands.length > 0 && (
                <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="brands-section">
                  <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Package className="w-5 h-5 text-orange-500" />
                        Marcas Disponibles ({brands.length})
                      </CardTitle>
                      {selectedBrand && (
                        <Button variant="ghost" size="sm" className="text-xs text-zinc-500 rounded-none" onClick={() => handleSelectBrand('')} data-testid="clear-brand-filter">
                          Limpiar filtro
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    {loadingBrands ? (
                      <div className="flex justify-center py-4"><CircleNotch className="w-6 h-6 text-orange-500 animate-spin" /></div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {brands.map((b) => {
                          const isActive = selectedBrand === b.brand;
                          const maxAvg = Math.max(...brands.map(x => x.avg_price));
                          const barPct = maxAvg > 0 ? (b.avg_price / maxAvg * 100) : 0;
                          return (
                            <div
                              key={b.brand}
                              onClick={() => handleSelectBrand(b.brand)}
                              className={`p-3 border cursor-pointer transition-all hover:shadow-sm ${
                                isActive
                                  ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20 shadow-sm'
                                  : 'border-zinc-200 dark:border-zinc-700 hover:border-orange-200'
                              }`}
                              data-testid={`brand-card-${b.brand.replace(/\s+/g, '-')}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <p className={`text-sm font-semibold truncate ${isActive ? 'text-orange-700 dark:text-orange-300' : 'text-zinc-900 dark:text-zinc-100'}`}>
                                  {b.brand}
                                </p>
                                <span className="text-xs text-zinc-400 flex-shrink-0 ml-2">{b.record_count} reg.</span>
                              </div>
                              <p className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{formatPrice(b.avg_price)}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                                <span className="text-green-600">{formatPrice(b.min_price)}</span>
                                <Minus className="w-3 h-3" />
                                <span className="text-red-600">{formatPrice(b.max_price)}</span>
                              </div>
                              <div className="w-full bg-zinc-200 dark:bg-zinc-700 h-1.5 mt-2 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${isActive ? 'bg-orange-500' : 'bg-zinc-400'}`}
                                  style={{ width: `${barPct}%` }}
                                />
                              </div>
                              {b.avg_price_3m > 0 && (
                                <p className="text-xs text-zinc-400 mt-1">Prom. 3m: {formatPrice(b.avg_price_3m)}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Historical Price Chart */}
              {chartData.length > 0 && (
                <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="price-chart">
                  <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ChartLineUp className="w-5 h-5 text-orange-500" />
                      Historial de Precios (2 años){selectedBrand ? ` — ${selectedBrand}` : ''}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={320}>
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="avgGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="month"
                          tickFormatter={formatMonth}
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          interval={Math.max(0, Math.floor(chartData.length / 8))}
                        />
                        <YAxis
                          tickFormatter={(v) => `$${v.toLocaleString('es-MX')}`}
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          width={70}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          wrapperStyle={{ fontSize: 12 }}
                          formatter={(value) => <span className="text-zinc-600 dark:text-zinc-400">{value}</span>}
                        />
                        <Area
                          type="monotone"
                          dataKey="avg_price"
                          name="Precio Promedio"
                          stroke="#f97316"
                          strokeWidth={2}
                          fill="url(#avgGradient)"
                        />
                        <Line
                          type="monotone"
                          dataKey="max_price"
                          name="Máximo"
                          stroke="#ef4444"
                          strokeWidth={1}
                          strokeDasharray="4 4"
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="min_price"
                          name="Mínimo"
                          stroke="#22c55e"
                          strokeWidth={1}
                          strokeDasharray="4 4"
                          dot={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Average price bar chart by month */}
              {chartData.length > 0 && (
                <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="avg-chart">
                  <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ChartLineUp className="w-5 h-5 text-blue-500" />
                      Promedio Mensual de Precios
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="month"
                          tickFormatter={formatMonth}
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          interval={Math.max(0, Math.floor(chartData.length / 8))}
                        />
                        <YAxis
                          tickFormatter={(v) => `$${v.toLocaleString('es-MX')}`}
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          width={70}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="avg_price" name="Precio Promedio" fill="#f97316" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Monthly data table */}
              {chartData.length > 0 && (
                <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="monthly-table">
                  <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-zinc-500" />
                      Datos Mensuales
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[280px]">
                      <table className="w-full text-sm">
                        <thead className="bg-zinc-100 dark:bg-zinc-800 sticky top-0">
                          <tr>
                            <th className="text-left p-3 font-medium text-zinc-600 dark:text-zinc-300">Mes</th>
                            <th className="text-right p-3 font-medium text-zinc-600 dark:text-zinc-300">Promedio</th>
                            <th className="text-right p-3 font-medium text-zinc-600 dark:text-zinc-300">Mínimo</th>
                            <th className="text-right p-3 font-medium text-zinc-600 dark:text-zinc-300">Máximo</th>
                            <th className="text-center p-3 font-medium text-zinc-600 dark:text-zinc-300">Registros</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                          {[...chartData].reverse().map((row, i) => {
                            const prev = chartData.length > 1 && i < chartData.length - 1
                              ? [...chartData].reverse()[i + 1]
                              : null;
                            const change = prev ? ((row.avg_price - prev.avg_price) / prev.avg_price * 100) : 0;
                            return (
                              <tr key={row.month} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                                <td className="p-3 text-zinc-900 dark:text-zinc-100 font-medium">{formatMonth(row.month)}</td>
                                <td className="p-3 text-right font-semibold text-zinc-900 dark:text-zinc-100">
                                  {formatPrice(row.avg_price)}
                                  {change !== 0 && (
                                    <span className={`ml-1 text-xs ${change > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                      {change > 0 ? '+' : ''}{change.toFixed(1)}%
                                    </span>
                                  )}
                                </td>
                                <td className="p-3 text-right text-green-600">{formatPrice(row.min_price)}</td>
                                <td className="p-3 text-right text-red-600">{formatPrice(row.max_price)}</td>
                                <td className="p-3 text-center text-zinc-500">{row.count}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* States comparison */}
              {availableStates.length > 0 && (
                <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="states-comparison">
                  <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-blue-500" />
                      Comparativa por Estado
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[250px]">
                      <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
                        {availableStates
                          .sort((a, b) => a.avg_price - b.avg_price)
                          .map((st, i) => {
                            const maxAvg = Math.max(...availableStates.map(s => s.avg_price));
                            const widthPct = maxAvg > 0 ? (st.avg_price / maxAvg * 100) : 0;
                            return (
                              <div key={st.name} className="p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-3 h-3 text-zinc-400" />
                                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{st.name}</span>
                                    <span className="text-xs text-zinc-400">({st.record_count} reg.)</span>
                                  </div>
                                  <span className="text-sm font-bold text-zinc-900 dark:text-zinc-100">{formatPrice(st.avg_price)}</span>
                                </div>
                                <div className="w-full bg-zinc-200 dark:bg-zinc-700 h-2 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-orange-400"
                                    style={{ width: `${widthPct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* No data message */}
              {chartData.length === 0 && (
                <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
                  <CardContent className="p-6 text-center text-zinc-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                    <p>No hay suficientes datos históricos para este material.</p>
                    <p className="text-xs mt-1">Los datos se van generando conforme los proveedores envían cotizaciones.</p>
                  </CardContent>
                </Card>
              )}

              {/* Active Alerts */}
              {alerts.length > 0 && (
                <Card className="border-blue-200 dark:border-blue-800 rounded-none" data-testid="alerts-list-card">
                  <CardHeader className="border-b border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-blue-800 dark:text-blue-300">
                      <Bell className="w-5 h-5" />
                      Mis Alertas de Precio ({alerts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-blue-100 dark:divide-blue-900">
                      {alerts.filter(a => a.active).map(alert => (
                        <div key={alert.alert_id} className="p-3 flex items-center justify-between" data-testid={`alert-${alert.alert_id}`}>
                          <div>
                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{alert.material_name}</p>
                            <p className="text-xs text-zinc-500">
                              {alert.alert_type === 'below' ? 'Por debajo de' : 'Por encima de'} ${alert.target_price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              {alert.state && ` — ${alert.state}`}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteAlert(alert.alert_id)} data-testid={`delete-alert-${alert.alert_id}`}>
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PriceConsultationPage;
